import OpenAI from 'openai';

// Initialize ChromaDB connection
let chromaCollection = null;

export const initializeRAG = async () => {
  try {
    const openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY || localStorage.getItem('openai_api_key');
    if (!openaiApiKey) {
      throw new Error('OpenAI API key not found');
    }

    console.log('✅ RAG initialization complete');
    return true;
  } catch (error) {
    console.error('❌ RAG initialization failed:', error);
    return false;
  }
};

export const searchDocuments = async (query, openaiClient) => {
  try {
    console.log(`🔍 Query: ${query}`);

    // Search ChromaDB via hierarchical backend API (backend handles embeddings and authority hierarchy)
    console.log('🏛️ CALLING HIERARCHICAL SEARCH ENDPOINT: /api/search_hierarchical');
    console.log('🏛️ Searching legal knowledge base with hierarchical authorities...');
    const searchResponse = await fetch('/api/search_hierarchical', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: query,
        n_results: 8
      })
    });

    if (!searchResponse.ok) {
      console.log('⚠️ Hierarchical search failed, falling back to original search...');
      // Fallback to original search endpoint
      const fallbackResponse = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query,
          n_results: 8
        })
      });

      if (!fallbackResponse.ok) {
        throw new Error(`Both hierarchical and fallback search failed: ${fallbackResponse.status}`);
      }

      const fallbackResults = await fallbackResponse.json();
      console.log('📚 Using fallback search results');

      return {
        documents: [fallbackResults.documents],
        metadatas: [fallbackResults.metadatas],
        distances: [fallbackResults.distances]
      };
    }

    const results = await searchResponse.json();

    if (results && results.documents && results.documents.length > 0) {
      console.log(`🏛️ Hierarchical search found ${results.documents.length} documents`);

      // Enhanced logging to show authority hierarchy
      console.log('📊 Hierarchical Search Results:');
      for (let i = 0; i < Math.min(5, results.documents.length); i++) {
        const distance = results.distances[i];
        const metadata = results.metadatas[i];
        const similarity = 1 - distance;
        const authority = metadata?.authority_level || 'unknown';
        const authorityRank = metadata?.authority_rank || '?';

        // Authority level display names
        const authorityNames = {
          'legal_interpretations': { name: 'LEGAL INT', emoji: '⚖️' },
          'code_and_regulations': { name: 'CODE/REG', emoji: '📘' },
          'revenue_rulings': 'REV RUL',
          'revenue_procedures': 'REV PROC',
          'private_letter_rulings': 'PLR',
          'secondary_authorities': { name: 'SECONDARY', emoji: '📚' },
          'state_tax_language': { name: 'STATE TAX', emoji: '🗺️' },
          'rules_for_reviewing': { name: 'RULES', emoji: '📋' },
          'other_authorities': 'OTHER',
          // Legacy mappings for backward compatibility
          'internal_revenue_code': 'IRC',
          'treasury_regulations': 'TREAS',
          'cases': 'CASE'
        };

        const authorityDisplay = authorityNames[authority]?.name || authorityNames[authority] || authority.toUpperCase();

        console.log(`  ${i + 1}. [${authorityDisplay}] ${metadata?.filename || 'Unknown'} (p.${metadata?.page_number || '?'}) - Similarity: ${similarity.toFixed(3)}`);
      }

      return {
        documents: [results.documents], // Wrap in array like Legal AI Assistant
        metadatas: [results.metadatas],
        distances: [results.distances]
      };
    } else {
      console.log('❌ No documents found in hierarchical knowledge base');
      return null;
    }

  } catch (error) {
    console.error('❌ Hierarchical search error:', error);

    // Try fallback to original search on error
    try {
      console.log('⚠️ Attempting fallback to original search...');
      const fallbackResponse = await fetch('/api/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query,
          n_results: 8
        })
      });

      if (fallbackResponse.ok) {
        const fallbackResults = await fallbackResponse.json();
        console.log('📚 Using fallback search results due to error');

        return {
          documents: [fallbackResults.documents],
          metadatas: [fallbackResults.metadatas],
          distances: [fallbackResults.distances]
        };
      }
    } catch (fallbackError) {
      console.error('❌ Fallback search also failed:', fallbackError);
    }

    return null;
  }
};

export const extractStructuredCitations = async (responseText, searchResults, openaiClient, selectedModel) => {
  if (!searchResults || !searchResults.documents) {
    return [];
  }

  const documents = searchResults.documents[0];
  const metadatas = searchResults.metadatas[0];
  const distances = searchResults.distances[0];

  // Prepare sources for quote extraction (same logic as Legal AI Assistant)
  const sourcesForExtraction = [];
  for (let i = 0; i < documents.length; i++) {
    const doc = documents[i];
    const metadata = metadatas[i];
    const distance = distances[i];
    const similarity = 1 - distance;

    if (similarity > 0.3) { // Only high-quality sources
      sourcesForExtraction.push({
        source_num: i + 1,
        content: doc,
        metadata: metadata,
        similarity: similarity
      });
    }
  }

  if (sourcesForExtraction.length === 0) {
    return [];
  }

  // Prepare sources text for GPT (exact same format as Legal AI Assistant)
  let sourcesText = "";
  for (const source of sourcesForExtraction) {
    sourcesText += `\n[Source ${source.source_num}]:\n${source.content}\n`;
  }

  // Quote extraction prompt (exact same as Legal AI Assistant)
  const extractionPrompt = `You are a Quote Extractor for legal citations. Your task is to:

1. Analyze the generated legal response
2. Find the most relevant supporting quotes from the provided sources
3. Extract VERBATIM quotes (exact text from sources) that support the response

GENERATED LEGAL RESPONSE:
${responseText}

SOURCE DOCUMENTS:
${sourcesText}

Please extract structured citations in the following JSON format:
{
    "citations": [
        {
            "source_num": 1,
            "verbatim_quote": "exact quote from source that supports the response"
        }
    ]
}

RULES:
- Only extract quotes that DIRECTLY support the response
- Quotes must be VERBATIM (exact text from source)
- Each quote should be 1-3 sentences maximum
- If a source doesn't support the response, don't include it
- Focus on the most important supporting evidence
- Do not add explanations or claims, just the quotes`;

  try {
    console.log('🔍 Extracting structured citations...');

    const extractionResponse = await openaiClient.chat.completions.create({
      model: selectedModel,
      messages: [
        {
          role: "system",
          content: "You are a precise legal quote extractor. Extract only verbatim quotes that directly support claims. Return valid JSON only."
        },
        {
          role: "user",
          content: extractionPrompt
        }
      ],
      temperature: 0.0,
      max_tokens: 1000
    });

    // Parse the JSON response (same cleanup as Legal AI Assistant)
    let extractedText = extractionResponse.choices[0].message.content.trim();

    if (extractedText.startsWith("```json")) {
      extractedText = extractedText.substring(7);
    }
    if (extractedText.endsWith("```")) {
      extractedText = extractedText.substring(0, extractedText.length - 3);
    }
    extractedText = extractedText.trim();

    const extractedCitations = JSON.parse(extractedText);

    // Enhance with metadata (same logic as Legal AI Assistant)
    const structuredCitations = [];
    for (const citation of extractedCitations.citations || []) {
      const sourceNum = citation.source_num;

      // Find corresponding source metadata
      const sourceMeta = sourcesForExtraction.find(source => source.source_num === sourceNum);

      if (sourceMeta) {
        structuredCitations.push({
          source_num: sourceNum,
          verbatim_quote: citation.verbatim_quote || '',
          file: sourceMeta.metadata?.filename || 'Unknown',
          page: sourceMeta.metadata?.page_number || 'Unknown',
          chapter: sourceMeta.metadata?.chapter_number || '',
          similarity: sourceMeta.similarity,
          authority_level: sourceMeta.metadata?.authority_level || 'other_authorities',
          authority_rank: sourceMeta.metadata?.authority_rank || 7
        });
      }
    }

    console.log(`✅ Extracted ${structuredCitations.length} structured citations`);
    return structuredCitations;

  } catch (error) {
    console.error('❌ Citation extraction error:', error);
    return [];
  }
};

export const formatRAGCitations = (citations) => {
  if (!citations || citations.length === 0) {
    return '';
  }

  let citationText = '\n\n---\n**🏛️ Sources from Legal Knowledge Base (Hierarchical Authority):**\n\n';

  // Authority level display names with emojis
  const authorityNames = {
    'legal_interpretations': { name: 'LEGAL INT', emoji: '⚖️' },
    'code_and_regulations': { name: 'CODE/REG', emoji: '📘' },
    'revenue_rulings': { name: 'REV RUL', emoji: '📜' },
    'revenue_procedures': { name: 'REV PROC', emoji: '📄' },
    'private_letter_rulings': { name: 'PLR', emoji: '💌' },
    'secondary_authorities': { name: 'SECONDARY', emoji: '📚' },
    'state_tax_language': { name: 'STATE TAX', emoji: '🗺️' },
    'rules_for_reviewing': { name: 'RULES', emoji: '📋' },
    'other_authorities': { name: 'OTHER', emoji: '📎' },
    // Legacy mappings
    'internal_revenue_code': { name: 'IRC', emoji: '🏛️' },
    'treasury_regulations': { name: 'TREAS', emoji: '📋' },
    'cases': { name: 'CASE', emoji: '⚖️' }
  };

  for (const citation of citations) {
    // Get authority info from citation metadata (if available)
    const authorityLevel = citation.authority_level || 'LEGACY_OLD_SYSTEM';
    const authorityInfo = authorityNames[authorityLevel] || { name: 'OLD_SYSTEM', emoji: '⚠️' };

    // Add explicit collection debug info
    const collectionInfo = citation.authority_level ?
      `[Collection: chroma_db_${authorityLevel}]` :
      `[Collection: UNKNOWN - USING OLD SYSTEM]`;

    citationText += `**${authorityInfo.emoji} [${authorityInfo.name}] ${collectionInfo} Source ${citation.source_num}:** ${citation.file} (Page ${citation.page}) - Similarity: ${citation.similarity.toFixed(3)}\n`;
    citationText += `*Quote:* "${citation.verbatim_quote}"\n\n`;
  }

  return citationText;
}; 
